import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Trophy, Crown, ArrowLeft, Zap } from "lucide-react";

/**
 * Final-score summary shown when the backend reports `status === "finished"`.
 *
 * The XP animation modal is owned by the parent (GamePage) — this component
 * only offers "XP Details" / "New Game" CTAs via props. `memo`-wrapped so
 * idle re-renders after game-end skip the work (KNOWN_ISSUES §1.36).
 */
function GameOverScreen({ game, onShowXP, onBackToLobby }) {
  const { t } = useTranslation();
  const winner = game.winner !== null ? game.players[game.winner] : null;
  const hasXP = game.xp_results && Object.keys(game.xp_results).length > 0;

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#E3F2FD] dark:bg-[#0a1428] p-4"
      data-testid="game-over-screen"
    >
      <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-2xl border-2 border-white/40">
        <Trophy className="w-12 sm:w-16 h-12 sm:h-16 mx-auto mb-3 text-[#FFC107]" />
        <h1
          className="text-3xl sm:text-4xl font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-2"
          style={{ fontFamily: "Fredoka" }}
        >
          {t("gameOver.title")}
        </h1>
        {winner && (
          <div className="mb-4">
            <p className="text-base text-[#1A237E]/70 dark:text-[#E3F2FD]/70 mb-1">{t("gameOver.winner")}</p>
            <p
              className="text-2xl sm:text-3xl font-bold text-[#38B6FF]"
              style={{ fontFamily: "Fredoka" }}
            >
              {winner.name}
            </p>
            <p className="text-sm text-[#1A237E]/60 dark:text-[#E3F2FD]/60">{t("gameOver.cryptoValue", { value: winner.crypto_value })}</p>
          </div>
        )}
        <div className="space-y-2 mb-4">
          {game.players.map((p, i) => (
            <div
              key={`p-${p.name}-${i}`}
              className={`flex items-center justify-between p-3 rounded-xl ${
                i === game.winner
                  ? "bg-[#FFC107]/20 border border-[#FFC107]/40"
                  : "bg-white/50 dark:bg-slate-900/50"
              }`}
            >
              <div className="flex items-center gap-2">
                {i === game.winner && <Crown className="w-4 h-4 text-[#FFC107]" />}
                <span className="font-bold text-[#1A237E] dark:text-[#E3F2FD] text-sm">{p.name}</span>
              </div>
              <span className="font-bold text-[#1A237E] dark:text-[#E3F2FD] text-sm">${p.crypto_value}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          {hasXP && (
            <Button
              data-testid="show-xp-btn"
              variant="outline"
              className="flex-1 rounded-full font-bold border-[#FFC107] text-[#FFC107]"
              onClick={onShowXP}
            >
              <Zap className="w-4 h-4 mr-1" /> {t("gameOver.button.xpDetails")}
            </Button>
          )}
          <Button
            data-testid="back-to-lobby-btn"
            className="flex-1 rounded-full bg-[#38B6FF] hover:bg-[#2196F3] text-white font-bold"
            onClick={onBackToLobby}
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> {t("gameOver.button.newGame")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default memo(GameOverScreen);
